import React, { useCallback, useEffect, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { Table, TableColumn, Progress } from '@backstage/core-components';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import BugReportIcon from '@material-ui/icons/BugReport';
import ListAltIcon from '@material-ui/icons/ListAlt';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import AssessmentIcon from '@material-ui/icons/Assessment';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import TimelineIcon from '@material-ui/icons/Timeline';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import HttpIcon from '@material-ui/icons/Http';
import StorageIcon from '@material-ui/icons/Storage';
import NotificationsIcon from '@material-ui/icons/Notifications';
import { Entity } from '@backstage/catalog-model';

export const NEWRELIC_APP_PREFIX_ANNOTATION = 'newrelic.com/app-prefix';

export const isNewRelicFacadesTabAvailable = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.[NEWRELIC_APP_PREFIX_ANNOTATION]);

// ─── Env helpers ─────────────────────────────────────────────────────────────

const DEV_ENVS = ['dev', 'development'];
const QA_ENVS = ['qa', 'uat', 'stage', 'staging', 'test'];
const PROD_ENVS = ['prod', 'production'];
const ALL_KNOWN_ENVS = [...DEV_ENVS, ...QA_ENVS, ...PROD_ENVS];

const bucketEnv = (env: string): 'dev' | 'qa' | 'production' | 'other' => {
  if (DEV_ENVS.includes(env)) return 'dev';
  if (QA_ENVS.includes(env)) return 'qa';
  if (PROD_ENVS.includes(env)) return 'production';
  return 'other';
};

const parseAppName = (
  fullName: string,
  prefix: string,
): { component: string; env: string } => {
  const withoutPrefix = fullName
    .toLowerCase()
    .replace(`${prefix.toLowerCase()}_`, '');
  const parts = withoutPrefix.split('_');
  const lastPart = parts[parts.length - 1];
  if (ALL_KNOWN_ENVS.includes(lastPart)) {
    return { component: parts.slice(0, -1).join('-'), env: lastPart };
  }
  return { component: withoutPrefix.replace(/_/g, '-'), env: 'unknown' };
};

// ─── Time ranges ─────────────────────────────────────────────────────────────

const TIME_RANGES = [
  { label: '15m', nrql: '15 minutes ago' },
  { label: '1h',  nrql: '1 hour ago' },
  { label: '6h',  nrql: '6 hours ago' },
  { label: '24h', nrql: '24 hours ago' },
  { label: '7d',  nrql: '7 days ago' },
] as const;

const LOG_LEVELS = ['all', 'ERROR', 'WARN', 'INFO', 'DEBUG'] as const;

// ─── Types ────────────────────────────────────────────────────────────────────

type AppRow = {
  id: string;
  appName: string;
  component: string;
  env: string;
  guid: string;
  accountId: number | null;
  alertSeverity: string;
  apdexScore: number | null;
  responseTime: number | null;
  errorRate: number | null;
  throughput: number | null;
};

type ErrorRow = {
  errorClass: string;
  count: number;
  message?: string;
};

type LogRow = {
  timestamp: number;
  level: string;
  message: string;
};

type TxRow = {
  name: string;
  avgMs: number;
  count: number;
};

type ServiceRelation = {
  name: string;
  guid: string;
  entityType: string;
  direction: 'upstream' | 'downstream';
  alertSeverity: string;
  responseTime: number | null;
  errorRate: number | null;
  throughput: number | null;
};

const ENV_GROUPS: {
  key: 'dev' | 'qa' | 'production' | 'other';
  label: string;
  color: string;
}[] = [
  { key: 'production', label: 'Production', color: '#c62828' },
  { key: 'qa',         label: 'QA / Staging', color: '#7b1fa2' },
  { key: 'dev',        label: 'Dev',           color: '#1976d2' },
  { key: 'other',      label: 'Other',         color: '#555' },
];

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles(theme => ({
  good:    { color: theme.palette.success?.main ?? '#4caf50', fontWeight: 600 },
  warn:    { color: theme.palette.warning?.main ?? '#ff9800', fontWeight: 600 },
  bad:     { color: theme.palette.error.main,                 fontWeight: 600 },
  neutral: { color: theme.palette.text.primary,               fontWeight: 600 },
  envChip: { fontWeight: 600, fontSize: '0.7rem', minWidth: 48 },
  sectionDivider: { margin: theme.spacing(3, 0, 1) },
  refreshRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1, 2),
  },
  tableSection: {
    '& .MuiTableSortLabel-root':                                  { color: 'inherit' },
    '& .MuiTableSortLabel-root:hover':                            { color: 'inherit' },
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active':         { color: 'inherit' },
    '& .MuiTableSortLabel-root.MuiTableSortLabel-active .MuiTableSortLabel-icon': { color: 'inherit' },
  },
  tabPanel:   { padding: theme.spacing(2) },
  detailControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  timeChip: {
    cursor: 'pointer',
  },
  nrLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    color: theme.palette.primary.main,
    textDecoration: 'none',
    fontSize: '0.85rem',
    '&:hover': { textDecoration: 'underline' },
  },
  logLevelError: { color: theme.palette.error.main,            fontWeight: 700, fontSize: '0.75rem' },
  logLevelWarn:  { color: theme.palette.warning?.main ?? '#ff9800', fontWeight: 700, fontSize: '0.75rem' },
  logLevelInfo:  { color: theme.palette.info?.main    ?? '#2196f3', fontWeight: 700, fontSize: '0.75rem' },
  logLevelDebug: { color: theme.palette.text.secondary,             fontWeight: 700, fontSize: '0.75rem' },
  logMsg:   { fontFamily: 'monospace', fontSize: '0.78rem', wordBreak: 'break-all' as any },
  emptyMsg: { padding: theme.spacing(4), textAlign: 'center' as any, color: theme.palette.text.secondary },
  envSelect: { minWidth: 160, marginRight: theme.spacing(1) },
  inlineLoader: { display: 'flex', justifyContent: 'center', padding: theme.spacing(4) },
}));

// ─── NRQL helper ─────────────────────────────────────────────────────────────

async function runNrql(
  proxyUrl: string,
  fetchApi: any,
  accountId: number,
  nrql: string,
): Promise<any[]> {
  const query = `{
    actor {
      account(id: ${accountId}) {
        nrql(query: ${JSON.stringify(nrql)}) {
          results
        }
      }
    }
  }`;
  const res = await fetchApi.fetch(`${proxyUrl}/newrelic/api/graphql`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  if (json.errors?.length) throw new Error(json.errors[0].message);
  return json.data?.actor?.account?.nrql?.results ?? [];
}

// ─── Tab info banner ──────────────────────────────────────────────────────────

const TabInfo = ({ title, description, tips }: {
  title: string;
  description: string;
  tips?: string[];
}) => (
  <Box mb={2} p={1.5} style={{
    background: 'rgba(21,101,192,0.05)',
    border: '1px solid rgba(21,101,192,0.15)',
    borderRadius: 6,
    display: 'flex',
    gap: 10,
    alignItems: 'flex-start',
  }}>
    <span style={{ fontSize: 16, lineHeight: 1.4, flexShrink: 0 }}>ℹ️</span>
    <Box>
      <Typography variant="body2" style={{ fontWeight: 700, marginBottom: 2 }}>{title}</Typography>
      <Typography variant="body2" style={{ opacity: 0.85, lineHeight: 1.5 }}>{description}</Typography>
      {tips && tips.length > 0 && (
        <Box mt={0.5}>
          {tips.map((tip, i) => (
            <Typography key={i} variant="caption" style={{ display: 'block', opacity: 0.7, lineHeight: 1.6 }}>
              • {tip}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  </Box>
);

const alertChipColor = (s: string): 'default' | 'primary' | 'secondary' =>
  s === 'CRITICAL' ? 'secondary' : s === 'WARNING' ? 'primary' : 'default';

// ─── Detail panel helpers ─────────────────────────────────────────────────────

const EnvSelector = ({
  rows,
  selected,
  onChange,
  classes,
}: {
  rows: AppRow[];
  selected: string;
  onChange: (v: string) => void;
  classes: ReturnType<typeof useStyles>;
}) => {
  const envOptions = Array.from(new Set(rows.map(r => r.env))).sort();
  return (
    <FormControl size="small" variant="outlined" className={classes.envSelect}>
      <Select
        value={selected}
        onChange={e => onChange(e.target.value as string)}
        displayEmpty
      >
        {envOptions.map(env => (
          <MenuItem key={env} value={env}>
            {env}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const TimeRangeSelector = ({
  selected,
  onChange,
  classes,
}: {
  selected: string;
  onChange: (v: string) => void;
  classes: ReturnType<typeof useStyles>;
}) => (
  <Box display="flex" style={{ gap: 4 }}>
    {TIME_RANGES.map(tr => (
      <Chip
        key={tr.label}
        label={tr.label}
        size="small"
        className={classes.timeChip}
        color={selected === tr.nrql ? 'primary' : 'default'}
        onClick={() => onChange(tr.nrql)}
      />
    ))}
  </Box>
);

// ─── Errors Panel ─────────────────────────────────────────────────────────────

const ErrorsPanel = ({
  rows,
  proxyUrl,
  fetchApi,
}: {
  rows: AppRow[];
  proxyUrl: string;
  fetchApi: any;
}) => {
  const classes = useStyles();
  const [selectedEnv, setSelectedEnv] = useState<string>(() => {
    const preferred = ['prod', 'production'];
    const match = rows.find(r => preferred.includes(r.env));
    return match?.env ?? rows[0]?.env ?? '';
  });
  const [timeRange, setTimeRange] = useState(TIME_RANGES[1].nrql); // 1h default
  const [errors, setErrors] = useState<ErrorRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const selectedRow = rows.find(r => r.env === selectedEnv);

  const fetch = useCallback(async () => {
    if (!selectedRow?.accountId) return;
    setLoading(true);
    setErr(null);
    try {
      const nrql = `FROM TransactionError SELECT count(*) as errorCount, latest(error.message) as errorMsg FACET error.class WHERE appName = '${selectedRow.appName}' SINCE ${timeRange} LIMIT 20`;
      const results = await runNrql(proxyUrl, fetchApi, selectedRow.accountId, nrql);
      setErrors(
        results.map(r => ({
          errorClass: r['error.class'] ?? r.facet?.[0] ?? 'Unknown',
          message:    r.errorMsg ?? '',
          count:      r.errorCount ?? 0,
        })),
      );
    } catch (e: any) {
      setErr(e.message ?? 'Failed to fetch error data');
    } finally {
      setLoading(false);
    }
  }, [selectedRow, timeRange, proxyUrl, fetchApi]);

  useEffect(() => { fetch(); }, [fetch]);

  const columns: TableColumn<ErrorRow>[] = [
    {
      title: 'Error Class',
      field: 'errorClass',
      render: row => (
        <Typography variant="body2" style={{ fontFamily: 'monospace', fontWeight: 600, color: '#c62828' }}>
          {row.errorClass}
        </Typography>
      ),
    },
    {
      title: 'Sample Message',
      field: 'message',
      render: row => (
        <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.78rem', opacity: 0.8 }}>
          {row.message || '—'}
        </Typography>
      ),
    },
    {
      title: 'Count',
      field: 'count',
      render: row => (
        <Typography variant="body2" className={classes.bad}>
          {row.count.toLocaleString()}
        </Typography>
      ),
    },
  ];

  const nrDeepLink = selectedRow
    ? `https://one.newrelic.com/redirect/entity/${selectedRow.guid}`
    : null;

  return (
    <Box>
      <Box className={classes.detailControls}>
        <EnvSelector rows={rows} selected={selectedEnv} onChange={v => setSelectedEnv(v)} classes={classes} />
        <TimeRangeSelector selected={timeRange} onChange={v => setTimeRange(v)} classes={classes} />
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetch} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {nrDeepLink && (
          <a href={`${nrDeepLink}`} target="_blank" rel="noopener noreferrer" className={classes.nrLink}>
            <OpenInNewIcon style={{ fontSize: 14 }} /> View Errors in New Relic
          </a>
        )}
      </Box>

      <TabInfo
        title="Top Errors"
        description="Shows the most frequent error classes thrown by this service in the selected time window, grouped by error type with a sample message. Data comes from the TransactionError event type in New Relic."
        tips={[
          'High counts on a single error class usually indicate a systemic issue — check the sample message for the root cause.',
          'Filter by environment to compare error rates across dev, staging and prod.',
          'Click "View Errors in New Relic" to see full stack traces and affected transactions.',
        ]}
      />

      {loading ? (
        <Box className={classes.inlineLoader}><CircularProgress size={28} /></Box>
      ) : err ? (
        <Typography color="error" style={{ padding: 16 }}>{err}</Typography>
      ) : errors.length === 0 ? (
        <Typography className={classes.emptyMsg}>No errors found in the selected time range.</Typography>
      ) : (
        <Box className={classes.tableSection}>
          <Table<ErrorRow>
            title={`Top Errors — ${selectedEnv} (${errors.length} classes)`}
            columns={columns}
            data={errors}
            options={{ paging: false, search: true, padding: 'dense', toolbar: true }}
          />
        </Box>
      )}
    </Box>
  );
};

// ─── Logs Panel ───────────────────────────────────────────────────────────────

const LogsPanel = ({
  rows,
  proxyUrl,
  fetchApi,
}: {
  rows: AppRow[];
  proxyUrl: string;
  fetchApi: any;
}) => {
  const classes = useStyles();
  const [selectedEnv, setSelectedEnv] = useState<string>(() => {
    const preferred = ['prod', 'production'];
    const match = rows.find(r => preferred.includes(r.env));
    return match?.env ?? rows[0]?.env ?? '';
  });
  const [timeRange, setTimeRange] = useState(TIME_RANGES[0].nrql); // 15m default
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const selectedRow = rows.find(r => r.env === selectedEnv);

  const fetch = useCallback(async () => {
    if (!selectedRow?.accountId) return;
    setLoading(true);
    setErr(null);
    try {
      // Keep level filter simple — no OR groups with dot-notation, no dot attrs in SELECT
      const levelClause = levelFilter !== 'all' ? `AND level = '${levelFilter}'` : '';
      const nrql = `FROM Log SELECT timestamp, level, message WHERE entity.name = '${selectedRow.appName}' ${levelClause} SINCE ${timeRange} LIMIT 100 ORDER BY timestamp DESC`;
      const results = await runNrql(proxyUrl, fetchApi, selectedRow.accountId, nrql);
      setLogs(
        results.map(r => ({
          timestamp: r.timestamp ?? 0,
          level:     (r.level ?? 'INFO').toUpperCase(),
          message:   r.message ?? r.MESSAGE ?? JSON.stringify(r),
        })),
      );
    } catch (e: any) {
      setErr(e.message ?? 'Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  }, [selectedRow, timeRange, levelFilter, proxyUrl, fetchApi]);

  useEffect(() => { fetch(); }, [fetch]);

  const levelClass = (level: string) => {
    if (level === 'ERROR' || level === 'FATAL') return classes.logLevelError;
    if (level === 'WARN'  || level === 'WARNING') return classes.logLevelWarn;
    if (level === 'INFO') return classes.logLevelInfo;
    return classes.logLevelDebug;
  };

  const nrDeepLink = selectedRow
    ? `https://one.newrelic.com/logger?&filters=%7B%22operator%22%3A%22AND%22%2C%22items%22%3A%5B%5D%7D`
    : null;

  return (
    <Box>
      <Box className={classes.detailControls}>
        <EnvSelector rows={rows} selected={selectedEnv} onChange={v => setSelectedEnv(v)} classes={classes} />
        <TimeRangeSelector selected={timeRange} onChange={v => setTimeRange(v)} classes={classes} />
        <Box display="flex" style={{ gap: 4 }}>
          {LOG_LEVELS.map(l => (
            <Chip
              key={l}
              label={l === 'all' ? 'All' : l}
              size="small"
              className={classes.timeChip}
              color={levelFilter === l ? 'primary' : 'default'}
              onClick={() => setLevelFilter(l)}
            />
          ))}
        </Box>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetch} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {nrDeepLink && (
          <a href={nrDeepLink} target="_blank" rel="noopener noreferrer" className={classes.nrLink}>
            <OpenInNewIcon style={{ fontSize: 14 }} /> Open Log Management
          </a>
        )}
      </Box>

      <TabInfo
        title="Application Logs"
        description="Streams the most recent log lines emitted by this service, ordered newest-first. Logs are matched by entity name so you only see entries from this application. Use the level filter to narrow down to errors or warnings."
        tips={[
          'Red rows are ERROR/FATAL — start here when debugging an incident.',
          'Logs only appear here if your app is configured to forward logs to New Relic (via agent log forwarding or Fluentd/Fluent Bit).',
          'Shorten the time window to 15m for real-time debugging during an active issue.',
        ]}
      />

      {loading ? (
        <Box className={classes.inlineLoader}><CircularProgress size={28} /></Box>
      ) : err ? (
        <Typography color="error" style={{ padding: 16 }}>{err}</Typography>
      ) : logs.length === 0 ? (
        <Typography className={classes.emptyMsg}>
          No logs found. Ensure your application is sending logs to New Relic with the entity name annotation.
        </Typography>
      ) : (
        <Box style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                <th style={{ padding: '6px 12px', textAlign: 'left', whiteSpace: 'nowrap', opacity: 0.6, fontWeight: 700 }}>Timestamp</th>
                <th style={{ padding: '6px 12px', textAlign: 'left', opacity: 0.6, fontWeight: 700 }}>Level</th>
                <th style={{ padding: '6px 12px', textAlign: 'left', opacity: 0.6, fontWeight: 700 }}>Message</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, idx) => (
                <tr
                  key={idx}
                  style={{
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    background: log.level === 'ERROR' || log.level === 'FATAL'
                      ? 'rgba(198,40,40,0.04)'
                      : log.level === 'WARN' || log.level === 'WARNING'
                      ? 'rgba(255,152,0,0.04)'
                      : 'transparent',
                  }}
                >
                  <td style={{ padding: '4px 12px', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.75rem', opacity: 0.7 }}>
                    {log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}
                  </td>
                  <td style={{ padding: '4px 12px', whiteSpace: 'nowrap' }}>
                    <span className={levelClass(log.level)}>{log.level}</span>
                  </td>
                  <td style={{ padding: '4px 12px' }}>
                    <span className={classes.logMsg}>{log.message}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}
    </Box>
  );
};

// ─── Transactions Panel ───────────────────────────────────────────────────────

const TransactionsPanel = ({
  rows,
  proxyUrl,
  fetchApi,
}: {
  rows: AppRow[];
  proxyUrl: string;
  fetchApi: any;
}) => {
  const classes = useStyles();
  const [selectedEnv, setSelectedEnv] = useState<string>(() => {
    const preferred = ['prod', 'production'];
    const match = rows.find(r => preferred.includes(r.env));
    return match?.env ?? rows[0]?.env ?? '';
  });
  const [timeRange, setTimeRange] = useState(TIME_RANGES[1].nrql); // 1h default
  const [txns, setTxns] = useState<TxRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const selectedRow = rows.find(r => r.env === selectedEnv);

  const fetch = useCallback(async () => {
    if (!selectedRow?.accountId) return;
    setLoading(true);
    setErr(null);
    try {
      const nrql = `FROM Transaction SELECT average(duration) as avgSec, count(*) as callCount WHERE appName = '${selectedRow.appName}' FACET name SINCE ${timeRange} LIMIT 25`;
      const results = await runNrql(proxyUrl, fetchApi, selectedRow.accountId, nrql);
      const parsed: TxRow[] = results
        .map(r => ({
          name:  r.name ?? r.facet?.[0] ?? 'Unknown',
          avgMs: (r.avgSec ?? 0) * 1000,
          count: r.callCount ?? 0,
        }))
        .sort((a, b) => b.avgMs - a.avgMs);
      setTxns(parsed);
    } catch (e: any) {
      setErr(e.message ?? 'Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  }, [selectedRow, timeRange, proxyUrl, fetchApi]);

  useEffect(() => { fetch(); }, [fetch]);

  const durationClass = (ms: number) => {
    if (ms < 200) return classes.good;
    if (ms < 1000) return classes.warn;
    return classes.bad;
  };

  const nrDeepLink = selectedRow
    ? `https://one.newrelic.com/redirect/entity/${selectedRow.guid}`
    : null;

  const columns: TableColumn<TxRow>[] = [
    {
      title: 'Transaction',
      field: 'name',
      render: row => (
        <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
          {row.name}
        </Typography>
      ),
    },
    {
      title: 'Avg Duration',
      field: 'avgMs',
      render: row => (
        <Typography variant="body2" className={durationClass(row.avgMs)}>
          {row.avgMs.toFixed(1)} ms
        </Typography>
      ),
    },
    {
      title: 'Calls',
      field: 'count',
      render: row => (
        <Typography variant="body2" className={classes.neutral}>
          {row.count.toLocaleString()}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <Box className={classes.detailControls}>
        <EnvSelector rows={rows} selected={selectedEnv} onChange={v => setSelectedEnv(v)} classes={classes} />
        <TimeRangeSelector selected={timeRange} onChange={v => setTimeRange(v)} classes={classes} />
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetch} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {nrDeepLink && (
          <a href={nrDeepLink} target="_blank" rel="noopener noreferrer" className={classes.nrLink}>
            <OpenInNewIcon style={{ fontSize: 14 }} /> View Transactions in New Relic
          </a>
        )}
      </Box>

      <TabInfo
        title="Slowest Transactions"
        description="Lists the top 25 web and background transactions sorted by average duration. Each row shows how long the transaction typically takes and how many times it was called. This helps you find which endpoints are the slowest and most worth optimising."
        tips={[
          'Green < 200ms · Orange 200ms–1s · Red > 1s — anything red is a candidate for optimisation.',
          'High call count + high duration = high user impact. Focus there first.',
          'Transaction names follow the pattern WebTransaction/Action/ControllerName or OtherTransaction/Background/JobName.',
        ]}
      />

      {loading ? (
        <Box className={classes.inlineLoader}><CircularProgress size={28} /></Box>
      ) : err ? (
        <Typography color="error" style={{ padding: 16 }}>{err}</Typography>
      ) : txns.length === 0 ? (
        <Typography className={classes.emptyMsg}>No transactions found in the selected time range.</Typography>
      ) : (
        <Box className={classes.tableSection}>
          <Table<TxRow>
            title={`Slowest Transactions — ${selectedEnv} (sorted by avg duration)`}
            columns={columns}
            data={txns}
            options={{ paging: false, search: true, padding: 'dense', toolbar: true }}
          />
        </Box>
      )}
    </Box>
  );
};

// ─── External Services Panel ─────────────────────────────────────────────────

type ExternalRow = {
  host: string;
  avgMs: number;
  callsPerMin: number;
  errorPct: number | null;
};

const ExternalServicesPanel = ({
  rows,
  proxyUrl,
  fetchApi,
}: {
  rows: AppRow[];
  proxyUrl: string;
  fetchApi: any;
}) => {
  const classes = useStyles();
  const [selectedEnv, setSelectedEnv] = useState<string>(() => {
    const pref = rows.find(r => ['prod', 'production'].includes(r.env));
    return pref?.env ?? rows[0]?.env ?? '';
  });
  const [timeRange, setTimeRange] = useState(TIME_RANGES[1].nrql);
  const [data, setData]   = useState<ExternalRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr]     = useState<string | null>(null);

  const selectedRow = rows.find(r => r.env === selectedEnv);

  const fetch = useCallback(async () => {
    if (!selectedRow?.accountId) return;
    setLoading(true);
    setErr(null);
    try {
      // duration is in seconds — multiply by 1000; avoid dot-notation inside functions
      const nrql = `FROM Span SELECT average(duration) as avgSec, count(*) as callCount WHERE appName = '${selectedRow.appName}' AND span.kind = 'client' AND http.url IS NOT NULL FACET http.url SINCE ${timeRange} LIMIT 30`;
      const results = await runNrql(proxyUrl, fetchApi, selectedRow.accountId, nrql);
      setData(
        results
          .map(r => ({
            host:        r['http.url'] ?? r.facet?.[0] ?? 'Unknown',
            avgMs:       (r.avgSec ?? 0) * 1000,
            callsPerMin: r.callCount ?? 0,
            errorPct:    null,
          }))
          .sort((a, b) => b.avgMs - a.avgMs),
      );
    } catch (e: any) {
      setErr(e.message ?? 'Failed to load external services');
    } finally {
      setLoading(false);
    }
  }, [selectedRow, timeRange, proxyUrl, fetchApi]);

  useEffect(() => { fetch(); }, [fetch]);

  const columns: TableColumn<ExternalRow>[] = [
    {
      title: 'External Host / URL',
      field: 'host',
      render: row => (
        <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.78rem', wordBreak: 'break-all' }}>
          {row.host}
        </Typography>
      ),
    },
    {
      title: 'Avg Latency',
      field: 'avgMs',
      render: row => (
        <Typography variant="body2" style={{ fontWeight: 700,
          color: row.avgMs > 1000 ? '#c62828' : row.avgMs > 300 ? '#f57c00' : '#2e7d32' }}>
          {row.avgMs >= 1000 ? `${(row.avgMs / 1000).toFixed(2)}s` : `${row.avgMs.toFixed(0)}ms`}
        </Typography>
      ),
    },
    {
      title: 'Calls/min',
      field: 'callsPerMin',
      render: row => (
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {row.callsPerMin.toFixed(1)}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <Box className={classes.detailControls}>
        <EnvSelector rows={rows} selected={selectedEnv} onChange={v => setSelectedEnv(v)} classes={classes} />
        <TimeRangeSelector selected={timeRange} onChange={v => setTimeRange(v)} classes={classes} />
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetch} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <TabInfo
        title="External HTTP / gRPC Calls"
        description="Shows every outbound HTTP or gRPC call this service makes to external hosts, ranked by average latency. This helps identify which third-party APIs or internal services are slowing down your application. Data is sourced from distributed trace spans with span.kind = 'client'."
        tips={[
          'Green < 300ms · Orange 300ms–1s · Red > 1s — a slow external call often accounts for most of a slow transaction.',
          'High calls/min to a slow endpoint is worth caching or batching.',
          'If the list is empty, ensure distributed tracing and span instrumentation are enabled in your APM agent config.',
        ]}
      />
      {loading ? (
        <Box className={classes.inlineLoader}><CircularProgress size={28} /></Box>
      ) : err ? (
        <Typography color="error" style={{ padding: 16 }}>{err}</Typography>
      ) : data.length === 0 ? (
        <Typography className={classes.emptyMsg}>
          No external HTTP calls found. Ensure distributed tracing / span instrumentation is enabled.
        </Typography>
      ) : (
        <Box className={classes.tableSection}>
          <Table<ExternalRow>
            title={`External Services — ${selectedEnv} (sorted by avg latency)`}
            columns={columns}
            data={data}
            options={{ paging: false, search: true, padding: 'dense', toolbar: true }}
          />
        </Box>
      )}
    </Box>
  );
};

// ─── Slow DB Queries Panel ────────────────────────────────────────────────────

type DbRow = {
  query: string;
  db: string;
  avgMs: number;
  count: number;
};

const DbQueriesPanel = ({
  rows,
  proxyUrl,
  fetchApi,
}: {
  rows: AppRow[];
  proxyUrl: string;
  fetchApi: any;
}) => {
  const classes = useStyles();
  const [selectedEnv, setSelectedEnv] = useState<string>(() => {
    const pref = rows.find(r => ['prod', 'production'].includes(r.env));
    return pref?.env ?? rows[0]?.env ?? '';
  });
  const [timeRange, setTimeRange] = useState(TIME_RANGES[1].nrql);
  const [data, setData]   = useState<DbRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr]     = useState<string | null>(null);

  const selectedRow = rows.find(r => r.env === selectedEnv);

  const fetch = useCallback(async () => {
    if (!selectedRow?.accountId) return;
    setLoading(true);
    setErr(null);
    try {
      const nrql = `FROM DatastoreSegment SELECT average(duration) as avgSec, count(*) as cnt WHERE appName = '${selectedRow.appName}' FACET statement, datastoreType SINCE ${timeRange} LIMIT 30`;
      const results = await runNrql(proxyUrl, fetchApi, selectedRow.accountId, nrql);
      setData(
        results
          .map(r => ({
            query:  r.statement ?? r.facet?.[0] ?? 'Unknown',
            db:     r.datastoreType ?? r.facet?.[1] ?? '—',
            avgMs:  (r.avgSec ?? 0) * 1000,
            count:  r.cnt ?? 0,
          }))
          .sort((a, b) => b.avgMs - a.avgMs),
      );
    } catch (e: any) {
      setErr(e.message ?? 'Failed to load DB queries');
    } finally {
      setLoading(false);
    }
  }, [selectedRow, timeRange, proxyUrl, fetchApi]);

  useEffect(() => { fetch(); }, [fetch]);

  const columns: TableColumn<DbRow>[] = [
    {
      title: 'Query',
      field: 'query',
      render: row => (
        <Tooltip title={row.query}>
          <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.75rem',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 420 }}>
            {row.query}
          </Typography>
        </Tooltip>
      ),
    },
    {
      title: 'DB Type',
      field: 'db',
      render: row => (
        <Chip label={row.db} size="small"
          style={{ background: '#558b2f', color: 'white', fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
      ),
    },
    {
      title: 'Avg Duration',
      field: 'avgMs',
      render: row => (
        <Typography variant="body2" style={{ fontWeight: 700,
          color: row.avgMs > 500 ? '#c62828' : row.avgMs > 100 ? '#f57c00' : '#2e7d32' }}>
          {row.avgMs >= 1000 ? `${(row.avgMs / 1000).toFixed(2)}s` : `${row.avgMs.toFixed(0)}ms`}
        </Typography>
      ),
    },
    {
      title: 'Total Calls',
      field: 'count',
      render: row => (
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {row.count.toLocaleString()}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <Box className={classes.detailControls}>
        <EnvSelector rows={rows} selected={selectedEnv} onChange={v => setSelectedEnv(v)} classes={classes} />
        <TimeRangeSelector selected={timeRange} onChange={v => setTimeRange(v)} classes={classes} />
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetch} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <TabInfo
        title="Slow Database Queries"
        description="Lists the top 30 database queries executed by this service, sorted by average duration. Queries are obfuscated (parameter values replaced with ?) so no sensitive data is exposed. Supports PostgreSQL, MySQL, MongoDB, Redis, and other datastores instrumented by the APM agent."
        tips={[
          'Green < 100ms · Orange 100ms–500ms · Red > 500ms — database calls over 500ms are typically a sign of a missing index or N+1 query.',
          'High total call count combined with moderate duration often indicates an N+1 problem — look for SELECT queries inside loops.',
          'Hover over a truncated query to see the full text. Use the search box to filter by table name.',
        ]}
      />
      {loading ? (
        <Box className={classes.inlineLoader}><CircularProgress size={28} /></Box>
      ) : err ? (
        <Typography color="error" style={{ padding: 16 }}>{err}</Typography>
      ) : data.length === 0 ? (
        <Typography className={classes.emptyMsg}>
          No database query data found. Ensure the New Relic APM agent is configured to capture datastore segments.
        </Typography>
      ) : (
        <Box className={classes.tableSection}>
          <Table<DbRow>
            title={`Slowest DB Queries — ${selectedEnv}`}
            columns={columns}
            data={data}
            options={{ paging: false, search: true, padding: 'dense', toolbar: true }}
          />
        </Box>
      )}
    </Box>
  );
};

// ─── Alert History Panel ──────────────────────────────────────────────────────

type AlertIncident = {
  incidentId: string;
  title: string;
  priority: string;
  state: string;
  openedAt: number;
  closedAt: number | null;
  durationMin: number | null;
};

const AlertHistoryPanel = ({
  rows,
  proxyUrl,
  fetchApi,
}: {
  rows: AppRow[];
  proxyUrl: string;
  fetchApi: any;
}) => {
  const classes = useStyles();
  const [selectedEnv, setSelectedEnv] = useState<string>(() => {
    const pref = rows.find(r => ['prod', 'production'].includes(r.env));
    return pref?.env ?? rows[0]?.env ?? '';
  });
  const [incidents, setIncidents] = useState<AlertIncident[]>([]);
  const [loading, setLoading]     = useState(false);
  const [err, setErr]             = useState<string | null>(null);

  const selectedRow = rows.find(r => r.env === selectedEnv);

  const fetch = useCallback(async () => {
    if (!selectedRow) return;
    setLoading(true);
    setErr(null);
    try {
      const query = `{
        actor {
          entity(guid: "${selectedRow.guid}") {
            ... on AlertableEntityOutline {
              alertSeverity
            }
            relatedEntities(filter: { relationshipTypes: { include: [HAS_ALERT_CONDITION] } }) {
              results {
                target {
                  entity {
                    name
                    guid
                    type
                  }
                }
              }
            }
          }
        }
      }`;
      // Use NRQL to query NrAiIncident for this entity
      if (!selectedRow.accountId) throw new Error('No account ID available');
      const nrql = `FROM NrAiIncident SELECT incidentId, title, priority, incidentEvent, timestamp WHERE entityGuid = '${selectedRow.guid}' SINCE 30 days ago LIMIT 100 ORDER BY timestamp DESC`;
      const results = await runNrql(proxyUrl, fetchApi, selectedRow.accountId, nrql);

      // Group open/close events by incidentId
      const incidentMap = new Map<string, Partial<AlertIncident>>();
      for (const r of results) {
        const id  = r.incidentId ?? '';
        const evt = r.incidentEvent ?? '';
        if (!incidentMap.has(id)) {
          incidentMap.set(id, {
            incidentId: id,
            title:      r.title ?? 'Unknown',
            priority:   r.priority ?? 'UNKNOWN',
            state:      'OPEN',
            openedAt:   0,
            closedAt:   null,
          });
        }
        const inc = incidentMap.get(id)!;
        if (evt === 'OPEN' || evt === 'CREATED') {
          inc.openedAt = r.timestamp ?? 0;
          inc.state    = 'OPEN';
        } else if (evt === 'CLOSE' || evt === 'RESOLVED') {
          inc.closedAt = r.timestamp ?? 0;
          inc.state    = 'CLOSED';
        }
      }

      const parsed: AlertIncident[] = Array.from(incidentMap.values())
        .filter((i): i is AlertIncident => !!i.incidentId)
        .map(i => ({
          ...i,
          durationMin: i.openedAt && i.closedAt
            ? Math.round((i.closedAt - i.openedAt) / 60000) : null,
        } as AlertIncident))
        .sort((a, b) => b.openedAt - a.openedAt);

      setIncidents(parsed);
    } catch (e: any) {
      setErr(e.message ?? 'Failed to load alert history');
    } finally {
      setLoading(false);
    }
  }, [selectedRow, proxyUrl, fetchApi]);

  useEffect(() => { fetch(); }, [fetch]);

  const priorityColor = (p: string) => {
    switch (p.toUpperCase()) {
      case 'CRITICAL': return '#c62828';
      case 'HIGH':     return '#e65100';
      case 'MEDIUM':   return '#f57c00';
      case 'LOW':      return '#558b2f';
      default:         return '#546e7a';
    }
  };

  const columns: TableColumn<AlertIncident>[] = [
    {
      title: 'Incident',
      field: 'title',
      render: row => (
        <Typography variant="body2" style={{ fontWeight: 600, fontSize: '0.82rem' }}>
          {row.title}
        </Typography>
      ),
    },
    {
      title: 'Priority',
      field: 'priority',
      render: row => (
        <Chip label={row.priority} size="small"
          style={{ background: priorityColor(row.priority), color: 'white',
            fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
      ),
    },
    {
      title: 'State',
      field: 'state',
      render: row => (
        <Chip label={row.state} size="small"
          style={{ background: row.state === 'OPEN' ? '#c62828' : '#2e7d32',
            color: 'white', fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
      ),
    },
    {
      title: 'Opened',
      field: 'openedAt',
      render: row => (
        <Typography variant="body2" style={{ fontSize: '0.78rem' }}>
          {row.openedAt ? new Date(row.openedAt).toLocaleString() : '—'}
        </Typography>
      ),
    },
    {
      title: 'Closed',
      field: 'closedAt',
      render: row => (
        <Typography variant="body2" style={{ fontSize: '0.78rem' }}>
          {row.closedAt ? new Date(row.closedAt).toLocaleString() : row.state === 'OPEN' ? (
            <span style={{ color: '#c62828', fontWeight: 700 }}>Still open</span>
          ) : '—'}
        </Typography>
      ),
    },
    {
      title: 'Duration',
      field: 'durationMin',
      render: row => (
        <Typography variant="body2" style={{ fontWeight: 600,
          color: row.durationMin != null && row.durationMin > 60 ? '#c62828' : 'inherit' }}>
          {row.durationMin != null
            ? row.durationMin >= 60
              ? `${Math.floor(row.durationMin / 60)}h ${row.durationMin % 60}m`
              : `${row.durationMin}m`
            : '—'}
        </Typography>
      ),
    },
  ];

  const openCount   = incidents.filter(i => i.state === 'OPEN').length;
  const critCount   = incidents.filter(i => i.priority.toUpperCase() === 'CRITICAL').length;

  return (
    <Box>
      <Box className={classes.detailControls}>
        <EnvSelector rows={rows} selected={selectedEnv} onChange={v => setSelectedEnv(v)} classes={classes} />
        <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.8rem' }}>Last 30 days</Typography>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetch} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {selectedRow && (
          <a href={`https://one.newrelic.com/redirect/entity/${selectedRow.guid}`}
            target="_blank" rel="noopener noreferrer" className={classes.nrLink}>
            <OpenInNewIcon style={{ fontSize: 14 }} /> View Alerts in New Relic
          </a>
        )}
      </Box>

      <TabInfo
        title="Alert Incident History"
        description="Shows all alert incidents that fired against this service in the last 30 days, sourced from the NrAiIncident event type. Each row shows when the incident opened, when (if) it was resolved, and how long it lasted. Red 'Still open' means the condition is currently alerting."
        tips={[
          'Sort by Opened to see the most recent incidents first. Look for patterns — recurring incidents at the same time of day often point to a scheduled job or traffic spike.',
          'Duration > 1 hour on a CRITICAL incident usually indicates an on-call page was triggered. Cross-reference with deployments to see if a recent release caused it.',
          'Incidents only appear here if alert conditions are configured in New Relic for this entity. If the list is always empty, check your alert policies.',
        ]}
      />

      {/* Summary chips */}
      {incidents.length > 0 && (
        <Box display="flex" style={{ gap: 8, marginBottom: 12 }}>
          <Chip label={`${incidents.length} total incidents`} size="small"
            style={{ background: '#546e7a', color: 'white', fontWeight: 600 }} />
          {openCount > 0 && (
            <Chip label={`${openCount} still open`} size="small"
              style={{ background: '#c62828', color: 'white', fontWeight: 700 }} />
          )}
          {critCount > 0 && (
            <Chip label={`${critCount} critical`} size="small"
              style={{ background: '#e65100', color: 'white', fontWeight: 700 }} />
          )}
        </Box>
      )}

      {loading ? (
        <Box className={classes.inlineLoader}><CircularProgress size={28} /></Box>
      ) : err ? (
        <Typography color="error" style={{ padding: 16 }}>{err}</Typography>
      ) : incidents.length === 0 ? (
        <Typography className={classes.emptyMsg}>
          No alert incidents found in the last 30 days for this entity.
        </Typography>
      ) : (
        <Box className={classes.tableSection}>
          <Table<AlertIncident>
            title={`Alert History — ${selectedEnv}`}
            columns={columns}
            data={incidents}
            options={{ paging: false, search: true, padding: 'dense', toolbar: true }}
          />
        </Box>
      )}
    </Box>
  );
};

// ─── Deployment History Panel ────────────────────────────────────────────────

type DeployRow = {
  timestamp: number;
  version: string;
  user: string;
  description: string;
  changeType: string;
  entityGuid: string;
};

const DeployPanel = ({
  rows,
  proxyUrl,
  fetchApi,
}: {
  rows: AppRow[];
  proxyUrl: string;
  fetchApi: any;
}) => {
  const classes = useStyles();
  const [selectedEnv, setSelectedEnv] = useState<string>(() => {
    const pref = rows.find(r => ['prod', 'production'].includes(r.env));
    return pref?.env ?? rows[0]?.env ?? '';
  });
  const [deploys, setDeploys]   = useState<DeployRow[]>([]);
  const [loading, setLoading]   = useState(false);
  const [err, setErr]           = useState<string | null>(null);
  // per-deploy before/after error rate
  const [comparison, setComparison] = useState<Record<number, { before: number | null; after: number | null }>>({});

  const selectedRow = rows.find(r => r.env === selectedEnv);

  const fetchDeploys = useCallback(async () => {
    if (!selectedRow) return;
    setLoading(true);
    setErr(null);
    setComparison({});
    try {
      // deploymentSearch lives on ApmApplicationEntity, not the generic Entity
      // interface, and takes no criteria argument — filter by date client-side.
      const query = `{
        actor {
          entity(guid: "${selectedRow.guid}") {
            ... on ApmApplicationEntity {
              deploymentSearch {
                results {
                  timestamp
                  version
                  user
                  description
                  changelog
                  entityGuid
                  deploymentType
                }
              }
            }
          }
        }
      }`;
      const res = await fetchApi.fetch(`${proxyUrl}/newrelic/api/graphql`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.errors?.length) throw new Error(json.errors[0].message);

      const cutoff = Date.now() - 30 * 24 * 3600 * 1000;
      const allResults: any[] = json.data?.actor?.entity?.deploymentSearch?.results ?? [];
      const results = allResults.filter(r => (r.timestamp ?? 0) >= cutoff);
      const parsed: DeployRow[] = results
        .map(r => ({
          timestamp:   r.timestamp ?? 0,
          version:     r.version ?? '—',
          user:        r.user ?? '—',
          description: r.description ?? r.changelog ?? '',
          changeType:  r.deploymentType ?? 'BASIC',
          entityGuid:  r.entityGuid ?? selectedRow.guid,
        }))
        .sort((a, b) => b.timestamp - a.timestamp);
      setDeploys(parsed);

      // Fetch before/after error rates for each deploy
      if (parsed.length > 0 && selectedRow.accountId) {
        const compMap: typeof comparison = {};
        await Promise.all(
          parsed.slice(0, 10).map(async (d, i) => {
            if (!selectedRow.accountId) return;
            const ts = d.timestamp;
            const window = 30 * 60 * 1000; // 30 min window
            const beforeNrql = `FROM Transaction SELECT percentage(count(*), WHERE error IS true) as errPct WHERE appName = '${selectedRow.appName}' SINCE ${ts - window} UNTIL ${ts}`;
            const afterNrql  = `FROM Transaction SELECT percentage(count(*), WHERE error IS true) as errPct WHERE appName = '${selectedRow.appName}' SINCE ${ts} UNTIL ${ts + window}`;
            try {
              const [before, after] = await Promise.all([
                runNrql(proxyUrl, fetchApi, selectedRow.accountId!, beforeNrql),
                runNrql(proxyUrl, fetchApi, selectedRow.accountId!, afterNrql),
              ]);
              compMap[i] = {
                before: before[0]?.errPct ?? null,
                after:  after[0]?.errPct ?? null,
              };
            } catch {
              compMap[i] = { before: null, after: null };
            }
          }),
        );
        setComparison(compMap);
      }
    } catch (e: any) {
      setErr(e.message ?? 'Failed to load deployments');
    } finally {
      setLoading(false);
    }
  }, [selectedRow, proxyUrl, fetchApi]);

  useEffect(() => { fetchDeploys(); }, [fetchDeploys]);

  const deployTypeColor = (t: string) => {
    switch (t) {
      case 'ROLLING': return '#1565c0';
      case 'BLUE_GREEN': return '#00695c';
      case 'CANARY': return '#e65100';
      case 'SHADOW': return '#6a1b9a';
      default: return '#546e7a';
    }
  };

  return (
    <Box>
      <Box className={classes.detailControls}>
        <EnvSelector rows={rows} selected={selectedEnv} onChange={v => setSelectedEnv(v)} classes={classes} />
        <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.8rem' }}>
          Last 30 days
        </Typography>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchDeploys} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {selectedRow && (
          <a href={`https://one.newrelic.com/redirect/entity/${selectedRow.guid}`}
            target="_blank" rel="noopener noreferrer" className={classes.nrLink}>
            <OpenInNewIcon style={{ fontSize: 14 }} /> View in New Relic
          </a>
        )}
      </Box>

      <TabInfo
        title="Deployment History"
        description="A chronological timeline of the last 30 days of deployments recorded in New Relic for this service. Each card shows the version, who deployed it, a description, and an automatic before/after error rate comparison (30-minute window around the deploy time) so you can instantly see if a release degraded production."
        tips={[
          'A ▲ red badge means error rate increased after this deploy — potential regression. A ▼ green badge means it improved.',
          'Deployments must be recorded via the New Relic deployment marker API (nr1 CLI, REST API, or a CI/CD step) to appear here.',
          'Deploy types: BASIC = standard release, ROLLING = rolling update, BLUE_GREEN = blue/green swap, CANARY = partial traffic shift.',
        ]}
      />

      {loading ? (
        <Box className={classes.inlineLoader}><CircularProgress size={28} /></Box>
      ) : err ? (
        <Typography color="error" style={{ padding: 16 }}>{err}</Typography>
      ) : deploys.length === 0 ? (
        <Typography className={classes.emptyMsg}>
          No deployments found in the last 30 days. Use the New Relic deployment marker API or CLI to record deployments.
        </Typography>
      ) : (
        <Box style={{ marginTop: 8 }}>
          {/* Timeline */}
          {deploys.map((d, i) => {
            const comp = comparison[i];
            const errDelta = comp?.before != null && comp?.after != null
              ? comp.after - comp.before : null;
            return (
              <Box key={i} display="flex" style={{ marginBottom: 0 }}>
                {/* Timeline spine */}
                <Box display="flex" flexDirection="column" alignItems="center"
                  style={{ width: 32, flexShrink: 0 }}>
                  <Box style={{ width: 12, height: 12, borderRadius: '50%', marginTop: 14,
                    background: deployTypeColor(d.changeType), flexShrink: 0, zIndex: 1 }} />
                  {i < deploys.length - 1 && (
                    <Box style={{ width: 2, flex: 1, background: 'rgba(0,0,0,0.1)', minHeight: 24 }} />
                  )}
                </Box>

                {/* Deploy card */}
                <Box flex={1} mb={1.5} p={1.5}
                  style={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: 6,
                    background: 'white', marginLeft: 8 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Chip label={d.changeType} size="small"
                        style={{ background: deployTypeColor(d.changeType), color: 'white',
                          fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
                      <Typography variant="body2" style={{ fontWeight: 700, fontFamily: 'monospace' }}>
                        v{d.version}
                      </Typography>
                    </Box>
                    <Typography variant="body2" style={{ fontSize: '0.75rem', opacity: 0.6 }}>
                      {new Date(d.timestamp).toLocaleString()}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" style={{ gap: 16, marginTop: 6 }}>
                    <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.8rem' }}>
                      👤 {d.user}
                    </Typography>
                    {d.description && (
                      <Typography variant="body2" style={{ fontSize: '0.8rem', opacity: 0.8 }}>
                        {d.description.slice(0, 120)}{d.description.length > 120 ? '…' : ''}
                      </Typography>
                    )}
                  </Box>
                  {/* Before/after comparison */}
                  {comp && (comp.before != null || comp.after != null) && (
                    <Box display="flex" alignItems="center" style={{ gap: 12, marginTop: 8 }}>
                      <Typography variant="caption" color="textSecondary">Error rate:</Typography>
                      <Typography variant="caption" style={{ fontWeight: 600 }}>
                        {comp.before != null ? `${comp.before.toFixed(2)}% before` : '—'}
                      </Typography>
                      <Typography variant="caption" style={{ opacity: 0.5 }}>→</Typography>
                      <Typography variant="caption" style={{ fontWeight: 600,
                        color: errDelta != null ? (errDelta > 0.5 ? '#c62828' : errDelta < -0.5 ? '#2e7d32' : 'inherit') : 'inherit' }}>
                        {comp.after != null ? `${comp.after.toFixed(2)}% after` : '—'}
                      </Typography>
                      {errDelta != null && Math.abs(errDelta) > 0.1 && (
                        <Chip
                          label={`${errDelta > 0 ? '▲' : '▼'} ${Math.abs(errDelta).toFixed(2)}%`}
                          size="small"
                          style={{
                            background: errDelta > 0.5 ? '#c62828' : '#2e7d32',
                            color: 'white', fontWeight: 700, fontSize: '0.65rem', height: 18,
                          }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

// ─── Distributed Traces Panel ────────────────────────────────────────────────

type TraceRow = {
  traceId: string;
  durationMs: number;
  spanCount: number;
  errorCount: number;
  timestamp: number;
  rootName: string;
};

type SpanRow = {
  id: string;
  name: string;
  durationMs: number;
  category: string;
  errorMsg: string | null;
  timestamp: number;
  parentId: string | null;
};

const TracesPanel = ({
  rows,
  proxyUrl,
  fetchApi,
}: {
  rows: AppRow[];
  proxyUrl: string;
  fetchApi: any;
}) => {
  const classes = useStyles();
  const [selectedEnv, setSelectedEnv] = useState<string>(() => {
    const pref = rows.find(r => ['prod', 'production'].includes(r.env));
    return pref?.env ?? rows[0]?.env ?? '';
  });
  const [timeRange, setTimeRange] = useState(TIME_RANGES[1].nrql);
  const [traces, setTraces]       = useState<TraceRow[]>([]);
  const [loading, setLoading]     = useState(false);
  const [err, setErr]             = useState<string | null>(null);
  const [selectedTrace, setSelectedTrace] = useState<TraceRow | null>(null);
  const [spans, setSpans]                 = useState<SpanRow[]>([]);
  const [spansLoading, setSpansLoading]   = useState(false);
  const [spansErr, setSpansErr]           = useState<string | null>(null);

  const selectedRow = rows.find(r => r.env === selectedEnv);

  const fetchTraces = useCallback(async () => {
    if (!selectedRow?.accountId) return;
    setLoading(true);
    setErr(null);
    setSelectedTrace(null);
    setSpans([]);
    try {
      // Use Span event type (DistributedTraceSummary is non-standard).
      // Avoid dot-notation inside aggregation functions — use duration * 1000
      // (NR stores span duration in seconds) and alias all computed columns.
      // Avoid filter() nested parens — derive error indicator from spans waterfall instead.
      const nrql = `FROM Span SELECT count(*) as spanCount, max(duration) as maxSec, latest(timestamp) as ts, latest(name) as rootName WHERE appName = '${selectedRow.appName}' FACET traceId SINCE ${timeRange} LIMIT 25`;
      const results = await runNrql(proxyUrl, fetchApi, selectedRow.accountId, nrql);
      setTraces(
        results.map(r => ({
          traceId:    r.traceId ?? r.facet?.[0] ?? '',
          durationMs: (r.maxSec ?? 0) * 1000,
          spanCount:  r.spanCount ?? 0,
          errorCount: 0, // derived from span waterfall on click
          timestamp:  r.ts ?? 0,
          rootName:   r.rootName ?? '—',
        })),
      );
    } catch (e: any) {
      setErr(e.message ?? 'Failed to load traces');
    } finally {
      setLoading(false);
    }
  }, [selectedRow, timeRange, proxyUrl, fetchApi]);

  useEffect(() => { fetchTraces(); }, [fetchTraces]);

  const fetchSpans = useCallback(async (trace: TraceRow) => {
    if (!selectedRow?.accountId) return;
    setSelectedTrace(trace);
    setSpansLoading(true);
    setSpansErr(null);
    try {
      // Backtick-escape dot-notation attributes so the NRQL parser doesn't
      // interpret them as method chains and report unbalanced parentheses.
      const nrql = `FROM Span SELECT name, duration as durSec, category, timestamp WHERE traceId = '${trace.traceId}' SINCE ${timeRange} LIMIT 100 ORDER BY timestamp ASC`;
      const results = await runNrql(proxyUrl, fetchApi, selectedRow.accountId, nrql);
      setSpans(
        results.map(r => ({
          id:         r.id ?? String(r.timestamp ?? Math.random()),
          name:       r.name ?? 'Unknown',
          durationMs: (r.durSec ?? 0) * 1000,
          category:   r.category ?? 'generic',
          errorMsg:   null,
          timestamp:  r.timestamp ?? 0,
          parentId:   null,
        })),
      );
    } catch (e: any) {
      setSpansErr(e.message ?? 'Failed to load spans');
    } finally {
      setSpansLoading(false);
    }
  }, [selectedRow, timeRange, proxyUrl, fetchApi]);

  // ── Span waterfall ──────────────────────────────────────────────────────────

  const SpanWaterfall = ({ spanList }: { spanList: SpanRow[] }) => {
    if (spanList.length === 0) return null;
    const minTs = Math.min(...spanList.map(s => s.timestamp));
    const totalMs = Math.max(...spanList.map(s => s.timestamp + s.durationMs)) - minTs;
    const catColor = (cat: string) => {
      switch (cat) {
        case 'http':        return '#1565c0';
        case 'datastore':
        case 'db':          return '#558b2f';
        case 'external':    return '#6a1b9a';
        case 'message':     return '#e65100';
        default:            return '#546e7a';
      }
    };
    return (
      <Box style={{ overflowX: 'auto', marginTop: 8 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
              {['Span', 'Category', 'Duration', 'Timeline'].map(h => (
                <th key={h} style={{ padding: '6px 10px', textAlign: 'left',
                  fontSize: '0.72rem', fontWeight: 700, opacity: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {spanList.map((s, i) => {
              const offsetPct = totalMs > 0 ? ((s.timestamp - minTs) / totalMs) * 100 : 0;
              const widthPct  = totalMs > 0 ? Math.max((s.durationMs / totalMs) * 100, 0.5) : 1;
              const color = s.errorMsg ? '#c62828' : catColor(s.category);
              return (
                <tr key={i} style={{ borderBottom: '1px solid rgba(0,0,0,0.05)',
                  background: s.errorMsg ? 'rgba(198,40,40,0.04)' : 'transparent' }}>
                  <td style={{ padding: '4px 10px', maxWidth: 280 }}>
                    <Tooltip title={s.errorMsg ?? s.name}>
                      <Typography variant="body2" style={{ fontFamily: 'monospace',
                        fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap', maxWidth: 270,
                        color: s.errorMsg ? '#c62828' : 'inherit' }}>
                        {s.name}
                      </Typography>
                    </Tooltip>
                  </td>
                  <td style={{ padding: '4px 10px' }}>
                    <span style={{ display: 'inline-block', padding: '1px 6px',
                      borderRadius: 3, background: color, color: 'white',
                      fontSize: '0.68rem', fontWeight: 700 }}>
                      {s.category}
                    </span>
                  </td>
                  <td style={{ padding: '4px 10px', whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" style={{ fontWeight: 600, fontSize: '0.78rem',
                      color: s.durationMs > 1000 ? '#c62828' : s.durationMs > 200 ? '#f57c00' : '#2e7d32' }}>
                      {s.durationMs >= 1000 ? `${(s.durationMs / 1000).toFixed(2)}s` : `${s.durationMs.toFixed(0)}ms`}
                    </Typography>
                  </td>
                  <td style={{ padding: '4px 10px', minWidth: 200 }}>
                    <div style={{ position: 'relative', height: 14, background: 'rgba(0,0,0,0.06)', borderRadius: 3 }}>
                      <div style={{
                        position: 'absolute', left: `${offsetPct}%`,
                        width: `${widthPct}%`, height: '100%',
                        background: color, borderRadius: 3, opacity: 0.85,
                        minWidth: 3,
                      }} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>
    );
  };

  const traceColumns: TableColumn<TraceRow>[] = [
    {
      title: 'Trace',
      field: 'traceId',
      render: row => (
        <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.75rem', opacity: 0.7 }}>
          {row.traceId.slice(0, 16)}…
        </Typography>
      ),
    },
    {
      title: 'Root Transaction',
      field: 'rootName',
      render: row => (
        <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 600 }}>
          {row.rootName}
        </Typography>
      ),
    },
    {
      title: 'Duration',
      field: 'durationMs',
      render: row => (
        <Typography variant="body2" style={{ fontWeight: 700,
          color: row.durationMs > 2000 ? '#c62828' : row.durationMs > 500 ? '#f57c00' : '#2e7d32' }}>
          {row.durationMs >= 1000 ? `${(row.durationMs / 1000).toFixed(2)}s` : `${row.durationMs.toFixed(0)}ms`}
        </Typography>
      ),
    },
    { title: 'Spans', field: 'spanCount',
      render: row => <Typography variant="body2">{row.spanCount}</Typography> },
    {
      title: 'Errors',
      field: 'errorCount',
      render: row => (
        <Typography variant="body2" style={{ fontWeight: row.errorCount > 0 ? 700 : 400,
          color: row.errorCount > 0 ? '#c62828' : 'inherit' }}>
          {row.errorCount > 0 ? row.errorCount : '—'}
        </Typography>
      ),
    },
    {
      title: 'Time',
      field: 'timestamp',
      render: row => (
        <Typography variant="body2" style={{ fontSize: '0.75rem', opacity: 0.7 }}>
          {row.timestamp ? new Date(row.timestamp).toLocaleTimeString() : '—'}
        </Typography>
      ),
    },
    {
      title: '',
      field: 'traceId',
      render: row => (
        <Chip label="Spans ▶" size="small" color="primary"
          style={{ cursor: 'pointer', fontSize: '0.7rem' }}
          onClick={() => fetchSpans(row)} />
      ),
    },
  ];

  return (
    <Box>
      <Box className={classes.detailControls}>
        <EnvSelector rows={rows} selected={selectedEnv} onChange={v => { setSelectedEnv(v); setSelectedTrace(null); }} classes={classes} />
        <TimeRangeSelector selected={timeRange} onChange={v => setTimeRange(v)} classes={classes} />
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchTraces} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <TabInfo
        title="Distributed Traces"
        description="Shows individual end-to-end request traces captured by New Relic distributed tracing. Each row is a single user request flowing through the system. The Duration column shows the total wall-clock time from the first span to the last. Click 'Spans ▶' on any trace to open a waterfall view that breaks the request down into every individual operation (HTTP calls, DB queries, background jobs)."
        tips={[
          'The waterfall Timeline bar shows where each span sits relative to the total trace duration — a gap at the start means waiting, a long bar means slow execution.',
          'Span colours: blue = HTTP, green = database, purple = external call, orange = message queue.',
          'Red rows in the waterfall = spans that threw an error. Hover the span name to see the error message.',
          'Traces are sampled — not every request is captured. Increase sampling rate in your APM agent config if you need more coverage.',
        ]}
      />

      {loading ? (
        <Box className={classes.inlineLoader}><CircularProgress size={28} /></Box>
      ) : err ? (
        <Typography color="error" style={{ padding: 16 }}>{err}</Typography>
      ) : traces.length === 0 ? (
        <Typography className={classes.emptyMsg}>
          No distributed traces found. Ensure distributed tracing is enabled.
        </Typography>
      ) : (
        <>
          <Box className={classes.tableSection}>
            <Table<TraceRow>
              title={`Recent Traces — ${selectedEnv} (click "Spans ▶" to view waterfall)`}
              columns={traceColumns}
              data={traces}
              options={{ paging: false, search: false, padding: 'dense', toolbar: true }}
            />
          </Box>

          {/* Span waterfall */}
          {selectedTrace && (
            <Box mt={2} p={2} style={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: 6 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  Span Waterfall — {selectedTrace.rootName}
                  <span style={{ fontFamily: 'monospace', fontWeight: 400, marginLeft: 8, opacity: 0.6, fontSize: '0.75rem' }}>
                    {selectedTrace.traceId}
                  </span>
                </Typography>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  {selectedRow && (
                    <a href={`https://one.newrelic.com/distributed-tracing?accountId=${selectedRow.accountId}&traceId=${selectedTrace.traceId}`}
                      target="_blank" rel="noopener noreferrer" className={classes.nrLink}>
                      <OpenInNewIcon style={{ fontSize: 14 }} /> Open in NR
                    </a>
                  )}
                  <IconButton size="small" onClick={() => setSelectedTrace(null)}>
                    <Typography style={{ fontSize: '0.75rem', lineHeight: 1 }}>✕</Typography>
                  </IconButton>
                </Box>
              </Box>
              {spansLoading ? (
                <Box className={classes.inlineLoader}><CircularProgress size={24} /></Box>
              ) : spansErr ? (
                <Typography color="error">{spansErr}</Typography>
              ) : (
                <SpanWaterfall spanList={spans} />
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

// ─── Service Map SVG ─────────────────────────────────────────────────────────

const SM_NODE_W = 190;
const SM_NODE_H = 58;
const SM_SVG_W  = 940;

const smAlertColor = (s: string) => {
  if (s === 'CRITICAL') return '#c62828';
  if (s === 'WARNING')  return '#f57c00';
  if (s === 'NOT_ALERTING') return '#2e7d32';
  return '#546e7a';
};

const smTypeColor = (t: string) => {
  switch (t) {
    case 'APPLICATION':    return '#1565c0';
    case 'BROWSER':        return '#6a1b9a';
    case 'MOBILE':         return '#00695c';
    case 'INFRASTRUCTURE': return '#4e342e';
    case 'HOST':           return '#4e342e';
    default:               return '#546e7a';
  }
};

const smTrunc = (s: string, n = 22) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

const ServiceMapDiagram = ({
  centerName,
  upstream,
  downstream,
}: {
  centerName: string;
  upstream: ServiceRelation[];
  downstream: ServiceRelation[];
}) => {
  const rows  = Math.max(upstream.length, downstream.length, 1);
  const svgH  = Math.max(rows * 88 + 70, 220);
  const LEFT_X   = 20;
  const CENTER_X = SM_SVG_W / 2 - SM_NODE_W / 2;
  const RIGHT_X  = SM_SVG_W - SM_NODE_W - 20;
  const centerY  = svgH / 2 - SM_NODE_H / 2;

  const nodeY = (idx: number, count: number) => {
    const spacing = svgH / (count + 1);
    return spacing * (idx + 1) - SM_NODE_H / 2;
  };

  const SmNode = ({
    x, y, rel, isCenter,
  }: {
    x: number; y: number; rel?: ServiceRelation; isCenter?: boolean;
  }) => {
    if (isCenter) {
      return (
        <g>
          <rect x={x} y={y} width={SM_NODE_W} height={SM_NODE_H} rx={8}
            fill="#1565c0" filter="url(#smShadow)" />
          <text x={x + SM_NODE_W / 2} y={y + 20} textAnchor="middle"
            fill="white" fontSize={9} fontWeight="bold" fontFamily="sans-serif">
            THIS SERVICE
          </text>
          <text x={x + SM_NODE_W / 2} y={y + 38} textAnchor="middle"
            fill="rgba(255,255,255,0.92)" fontSize={11} fontWeight="600" fontFamily="sans-serif">
            {smTrunc(centerName)}
          </text>
        </g>
      );
    }
    if (!rel) return null;
    const color = smAlertColor(rel.alertSeverity);
    const hdrColor = smTypeColor(rel.entityType);
    return (
      <g>
        <rect x={x} y={y} width={SM_NODE_W} height={SM_NODE_H} rx={6}
          fill="white" stroke={color} strokeWidth={1.5} filter="url(#smShadow)" />
        {/* coloured type header bar */}
        <rect x={x} y={y} width={SM_NODE_W} height={16} rx={6} fill={hdrColor} />
        <rect x={x} y={y + 8} width={SM_NODE_W} height={8} fill={hdrColor} />
        <text x={x + SM_NODE_W / 2} y={y + 11} textAnchor="middle"
          fill="white" fontSize={9} fontWeight="bold" fontFamily="sans-serif">
          {rel.entityType}
        </text>
        <text x={x + 8} y={y + 30} fill="#212121" fontSize={11}
          fontWeight="600" fontFamily="sans-serif">
          {smTrunc(rel.name)}
        </text>
        <text x={x + 8} y={y + 46} fill="#666" fontSize={9} fontFamily="sans-serif">
          {rel.responseTime != null ? `${rel.responseTime.toFixed(0)} ms` : '—'}
          {rel.errorRate != null ? `   err ${(rel.errorRate * 100).toFixed(1)}%` : ''}
        </text>
      </g>
    );
  };

  return (
    <svg viewBox={`0 0 ${SM_SVG_W} ${svgH}`}
      style={{ width: '100%', maxWidth: SM_SVG_W, display: 'block', overflow: 'visible' }}>
      <defs>
        <marker id="smArrow" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
          <polygon points="0 0,8 3,0 6" fill="#607d8b" />
        </marker>
        <marker id="smArrowRed" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
          <polygon points="0 0,8 3,0 6" fill="#c62828" />
        </marker>
        <filter id="smShadow" x="-10%" y="-10%" width="130%" height="130%">
          <feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="rgba(0,0,0,0.12)" />
        </filter>
      </defs>

      {/* Section labels */}
      {upstream.length > 0 && (
        <text x={LEFT_X + SM_NODE_W / 2} y={14} textAnchor="middle"
          fill="#78909c" fontSize={9} fontWeight="700" fontFamily="sans-serif"
          letterSpacing="0.08em">
          UPSTREAM CALLERS
        </text>
      )}
      {downstream.length > 0 && (
        <text x={RIGHT_X + SM_NODE_W / 2} y={14} textAnchor="middle"
          fill="#78909c" fontSize={9} fontWeight="700" fontFamily="sans-serif"
          letterSpacing="0.08em">
          DOWNSTREAM DEPS
        </text>
      )}

      {/* Upstream → center lines */}
      {upstream.map((u, i) => {
        const fy = nodeY(i, upstream.length) + SM_NODE_H / 2;
        const fx = LEFT_X + SM_NODE_W;
        const ty = centerY + SM_NODE_H / 2;
        const tx = CENTER_X;
        const mx = (fx + tx) / 2;
        const alert = u.alertSeverity === 'CRITICAL' || u.alertSeverity === 'WARNING';
        return (
          <path key={`up-${u.guid}`}
            d={`M ${fx} ${fy} C ${mx} ${fy}, ${mx} ${ty}, ${tx} ${ty}`}
            fill="none" stroke={alert ? '#c62828' : '#90a4ae'}
            strokeWidth={alert ? 2 : 1.5}
            strokeDasharray={alert ? '5,3' : undefined}
            markerEnd={`url(#${alert ? 'smArrowRed' : 'smArrow'})`}
            opacity={0.75} />
        );
      })}

      {/* Center → downstream lines */}
      {downstream.map((d, i) => {
        const fy = centerY + SM_NODE_H / 2;
        const fx = CENTER_X + SM_NODE_W;
        const ty = nodeY(i, downstream.length) + SM_NODE_H / 2;
        const tx = RIGHT_X;
        const mx = (fx + tx) / 2;
        const alert = d.alertSeverity === 'CRITICAL' || d.alertSeverity === 'WARNING';
        return (
          <path key={`dn-${d.guid}`}
            d={`M ${fx} ${fy} C ${mx} ${fy}, ${mx} ${ty}, ${tx} ${ty}`}
            fill="none" stroke={alert ? '#c62828' : '#90a4ae'}
            strokeWidth={alert ? 2 : 1.5}
            strokeDasharray={alert ? '5,3' : undefined}
            markerEnd={`url(#${alert ? 'smArrowRed' : 'smArrow'})`}
            opacity={0.75} />
        );
      })}

      {/* Upstream nodes */}
      {upstream.map((u, i) => (
        <SmNode key={u.guid} x={LEFT_X} y={nodeY(i, upstream.length)} rel={u} />
      ))}

      {/* Center node */}
      <SmNode x={CENTER_X} y={centerY} isCenter centerName={centerName} />

      {/* Downstream nodes */}
      {downstream.map((d, i) => (
        <SmNode key={d.guid} x={RIGHT_X} y={nodeY(i, downstream.length)} rel={d} />
      ))}
    </svg>
  );
};

// ─── Service Map Panel ────────────────────────────────────────────────────────

const ServiceMapPanel = ({
  rows,
  proxyUrl,
  fetchApi,
}: {
  rows: AppRow[];
  proxyUrl: string;
  fetchApi: any;
}) => {
  const classes = useStyles();
  const [selectedEnv, setSelectedEnv] = useState<string>(() => {
    const preferred = ['prod', 'production'];
    const match = rows.find(r => preferred.includes(r.env));
    return match?.env ?? rows[0]?.env ?? '';
  });
  const [relations, setRelations] = useState<ServiceRelation[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const selectedRow = rows.find(r => r.env === selectedEnv);

  const fetch = useCallback(async () => {
    if (!selectedRow) return;
    setLoading(true);
    setErr(null);
    try {
      const query = `{
        actor {
          entity(guid: "${selectedRow.guid}") {
            relatedEntities(filter: {
              relationshipTypes: { include: [CALLS, CALLED_BY] }
            }) {
              results {
                type
                source {
                  entity {
                    name guid type alertSeverity
                    ... on ApmApplicationEntity {
                      apmSummary { responseTimeAverage errorRate throughput }
                    }
                  }
                }
                target {
                  entity {
                    name guid type alertSeverity
                    ... on ApmApplicationEntity {
                      apmSummary { responseTimeAverage errorRate throughput }
                    }
                  }
                }
              }
            }
          }
        }
      }`;
      const res = await fetchApi.fetch(`${proxyUrl}/newrelic/api/graphql`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.errors?.length) throw new Error(json.errors[0].message);

      const results: any[] = json.data?.actor?.entity?.relatedEntities?.results ?? [];
      const seen = new Set<string>();
      const parsed: ServiceRelation[] = [];

      for (const r of results) {
        const isOutgoing = r.source?.entity?.guid === selectedRow.guid;
        const other = isOutgoing ? r.target?.entity : r.source?.entity;
        if (!other?.guid) continue;
        const direction: 'upstream' | 'downstream' = isOutgoing ? 'downstream' : 'upstream';
        const key = `${other.guid}:${direction}`;
        if (seen.has(key)) continue;
        seen.add(key);
        parsed.push({
          name:         other.name ?? 'Unknown',
          guid:         other.guid,
          entityType:   other.type ?? 'UNKNOWN',
          direction,
          alertSeverity: other.alertSeverity ?? 'NOT_CONFIGURED',
          responseTime: other.apmSummary?.responseTimeAverage ?? null,
          errorRate:    other.apmSummary?.errorRate ?? null,
          throughput:   other.apmSummary?.throughput ?? null,
        });
      }
      setRelations(parsed);
    } catch (e: any) {
      setErr(e.message ?? 'Failed to load service map');
    } finally {
      setLoading(false);
    }
  }, [selectedRow, proxyUrl, fetchApi]);

  useEffect(() => { fetch(); }, [fetch]);

  const upstream   = relations.filter(r => r.direction === 'upstream');
  const downstream = relations.filter(r => r.direction === 'downstream');

  const relColumns: TableColumn<ServiceRelation>[] = [
    {
      title: 'Service',
      field: 'name',
      render: row => (
        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
          <Chip label={row.entityType} size="small"
            style={{ background: smTypeColor(row.entityType), color: 'white',
              fontWeight: 700, fontSize: '0.65rem', height: 18 }} />
          <Typography variant="body2" style={{ fontWeight: 600, fontFamily: 'monospace', fontSize: '0.8rem' }}>
            {row.name}
          </Typography>
          {row.guid && (
            <Tooltip title="Open in New Relic">
              <IconButton size="small" component="a"
                href={`https://one.newrelic.com/redirect/entity/${row.guid}`}
                target="_blank" rel="noopener noreferrer">
                <OpenInNewIcon style={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      title: 'Alert',
      field: 'alertSeverity',
      render: row => row.alertSeverity && row.alertSeverity !== 'NOT_CONFIGURED' ? (
        <Chip label={row.alertSeverity} size="small"
          color={alertChipColor(row.alertSeverity)} />
      ) : (
        <Typography variant="body2" color="textSecondary">—</Typography>
      ),
    },
    {
      title: 'Avg Response',
      field: 'responseTime',
      render: row => (
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {row.responseTime != null ? `${row.responseTime.toFixed(0)} ms` : '—'}
        </Typography>
      ),
    },
    {
      title: 'Error Rate',
      field: 'errorRate',
      render: row => (
        <Typography variant="body2"
          style={{ fontWeight: 600, color: row.errorRate != null && row.errorRate >= 0.05 ? '#c62828' : undefined }}>
          {row.errorRate != null ? `${(row.errorRate * 100).toFixed(2)}%` : '—'}
        </Typography>
      ),
    },
    {
      title: 'Throughput',
      field: 'throughput',
      render: row => (
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {row.throughput != null ? `${row.throughput.toFixed(0)} rpm` : '—'}
        </Typography>
      ),
    },
  ];

  return (
    <Box>
      <Box className={classes.detailControls}>
        <EnvSelector rows={rows} selected={selectedEnv} onChange={v => setSelectedEnv(v)} classes={classes} />
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetch} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {selectedRow && (
          <a href={`https://one.newrelic.com/redirect/entity/${selectedRow.guid}`}
            target="_blank" rel="noopener noreferrer" className={classes.nrLink}>
            <OpenInNewIcon style={{ fontSize: 14 }} /> View Service Map in New Relic
          </a>
        )}
      </Box>

      <TabInfo
        title="Service Map — API Call Chain"
        description="Visualises the live call graph for this service: who calls it (upstream) and what it calls (downstream). The SVG diagram in the centre shows this service with arrows connecting to every dependent service detected via distributed tracing. Dashed red arrows indicate a dependency that is currently in a CRITICAL or WARNING alert state."
        tips={[
          'Each node shows avg response time and error rate from the last APM summary poll — a slow downstream node is a likely bottleneck.',
          'Node colour key: blue = APM Application, teal = Mobile, purple = Browser, brown = Infrastructure/Host.',
          'Relationships are detected automatically from distributed trace spans — if a service is missing, enable distributed tracing on both sides.',
          'Click the ↗ icon on any node in the detail tables below to jump directly to that service in New Relic.',
        ]}
      />

      {loading ? (
        <Box className={classes.inlineLoader}><CircularProgress size={28} /></Box>
      ) : err ? (
        <Typography color="error" style={{ padding: 16 }}>{err}</Typography>
      ) : relations.length === 0 ? (
        <Typography className={classes.emptyMsg}>
          No service relationships found. Ensure distributed tracing is enabled for this application.
        </Typography>
      ) : (
        <>
          {/* SVG diagram */}
          <Box style={{ padding: '8px 0 24px', overflowX: 'auto' }}>
            <ServiceMapDiagram
              centerName={selectedRow?.appName ?? selectedEnv}
              upstream={upstream}
              downstream={downstream}
            />
          </Box>

          {/* Detail tables */}
          {upstream.length > 0 && (
            <Box className={classes.tableSection} mb={2}>
              <Table<ServiceRelation>
                title={
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <span style={{ display: 'inline-block', width: 10, height: 10,
                      borderRadius: '50%', background: '#546e7a' }} />
                    <Typography variant="h6" style={{ fontWeight: 700 }}>
                      Upstream Callers
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      ({upstream.length} services call this)
                    </Typography>
                  </Box>
                }
                columns={relColumns}
                data={upstream}
                options={{ paging: false, search: false, padding: 'dense', toolbar: true }}
              />
            </Box>
          )}

          {downstream.length > 0 && (
            <Box className={classes.tableSection}>
              <Table<ServiceRelation>
                title={
                  <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                    <span style={{ display: 'inline-block', width: 10, height: 10,
                      borderRadius: '50%', background: '#1565c0' }} />
                    <Typography variant="h6" style={{ fontWeight: 700 }}>
                      Downstream Dependencies
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      (this service calls {downstream.length} others)
                    </Typography>
                  </Box>
                }
                columns={relColumns}
                data={downstream}
                options={{ paging: false, search: false, padding: 'dense', toolbar: true }}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const NewRelicFacadesTab = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [rows, setRows] = useState<AppRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [proxyUrl, setProxyUrl] = useState<string>('');
  const [activeTab, setActiveTab] = useState(0);

  const prefix =
    entity.metadata.annotations?.[NEWRELIC_APP_PREFIX_ANNOTATION] ?? '';

  const fetchData = useCallback(async () => {
    if (!prefix) return;
    setLoading(true);
    setError(null);
    try {
      const base = await discoveryApi.getBaseUrl('proxy');
      setProxyUrl(base);

      // Step 1: find all apps with this prefix
      const searchQuery = `{
        actor {
          entitySearch(query: "name LIKE '${prefix}%' AND type = 'APPLICATION'") {
            results {
              entities {
                guid
                name
              }
            }
          }
        }
      }`;

      const searchRes = await fetchApi.fetch(
        `${base}/newrelic/api/graphql`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: searchQuery }),
        },
      );
      if (!searchRes.ok) throw new Error(`HTTP ${searchRes.status}`);
      const searchJson = await searchRes.json();
      const entities: { guid: string; name: string }[] =
        searchJson.data?.actor?.entitySearch?.results?.entities ?? [];

      if (!entities.length) {
        setRows([]);
        setLoading(false);
        return;
      }

      // Step 2: fetch metrics + accountId in chunks of 25 (NerdGraph limit)
      const CHUNK_SIZE = 25;
      const guids = entities.map(e => e.guid);
      const chunks: string[][] = [];
      for (let i = 0; i < guids.length; i += CHUNK_SIZE) {
        chunks.push(guids.slice(i, i + CHUNK_SIZE));
      }

      const metricsEntities: any[] = [];
      for (const chunk of chunks) {
        const guidsArg = chunk.map(g => `"${g}"`).join(', ');
        const metricsQuery = `{
          actor {
            entities(guids: [${guidsArg}]) {
              name
              guid
              accountId
              alertSeverity
              ... on ApmApplicationEntity {
                apmSummary {
                  apdexScore
                  errorRate
                  responseTimeAverage
                  throughput
                }
              }
            }
          }
        }`;

        const metricsRes = await fetchApi.fetch(
          `${base}/newrelic/api/graphql`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ query: metricsQuery }),
          },
        );
        if (!metricsRes.ok) throw new Error(`HTTP ${metricsRes.status}`);
        const metricsJson = await metricsRes.json();
        metricsEntities.push(...(metricsJson.data?.actor?.entities ?? []));
      }

      const result: AppRow[] = metricsEntities
        .map(e => {
          const { component, env } = parseAppName(e.name, prefix);
          return {
            id:            e.guid,
            appName:       e.name,
            component,
            env,
            guid:          e.guid,
            accountId:     e.accountId ?? null,
            alertSeverity: e.alertSeverity ?? 'NOT_CONFIGURED',
            apdexScore:    e.apmSummary?.apdexScore ?? null,
            responseTime:  e.apmSummary?.responseTimeAverage ?? null,
            errorRate:     e.apmSummary?.errorRate ?? null,
            throughput:    e.apmSummary?.throughput ?? null,
          };
        })
        .sort((a, b) => a.component.localeCompare(b.component));

      setRows(result);
    } catch (e: any) {
      setError(e.message ?? 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [prefix, discoveryApi, fetchApi]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Overview metric helpers ──────────────────────────────────────────────

  const apdexClass = (v: number | null) => {
    if (v === null) return classes.neutral;
    if (v >= 0.9) return classes.good;
    if (v >= 0.7) return classes.warn;
    return classes.bad;
  };

  const errorClass = (v: number | null) => {
    if (v === null) return classes.neutral;
    if (v < 0.01) return classes.good;
    if (v < 0.05) return classes.warn;
    return classes.bad;
  };

  const overviewColumns: TableColumn<AppRow>[] = [
    {
      title: 'Component',
      field: 'component',
      render: row => (
        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {row.component}
          </Typography>
          <Tooltip title={`Open ${row.appName} in New Relic`}>
            <IconButton
              size="small"
              component="a"
              href={`https://one.newrelic.com/redirect/entity/${row.guid}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <OpenInNewIcon style={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      title: 'Alert',
      field: 'alertSeverity',
      render: row =>
        row.alertSeverity && row.alertSeverity !== 'NOT_CONFIGURED' ? (
          <Chip
            label={row.alertSeverity}
            size="small"
            color={alertChipColor(row.alertSeverity)}
          />
        ) : (
          <Typography variant="body2" color="textSecondary">—</Typography>
        ),
    },
    {
      title: 'Apdex',
      field: 'apdexScore',
      render: row => (
        <Typography variant="body2" className={apdexClass(row.apdexScore)}>
          {row.apdexScore != null ? row.apdexScore.toFixed(2) : '—'}
        </Typography>
      ),
    },
    {
      title: 'Response Time',
      field: 'responseTime',
      render: row => (
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {row.responseTime != null ? `${row.responseTime.toFixed(0)} ms` : '—'}
        </Typography>
      ),
    },
    {
      title: 'Error Rate',
      field: 'errorRate',
      render: row => (
        <Typography variant="body2" className={errorClass(row.errorRate)}>
          {row.errorRate != null ? `${(row.errorRate * 100).toFixed(2)}%` : '—'}
        </Typography>
      ),
    },
    {
      title: 'Throughput',
      field: 'throughput',
      render: row => (
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {row.throughput != null ? `${row.throughput.toFixed(0)} rpm` : '—'}
        </Typography>
      ),
    },
  ];

  if (loading) return <Progress />;

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // ── Group overview rows ──────────────────────────────────────────────────

  const grouped = new Map<string, AppRow[]>();
  for (const group of ENV_GROUPS) {
    grouped.set(group.key, []);
  }
  for (const row of rows) {
    const bucket = bucketEnv(row.env);
    grouped.get(bucket)!.push(row);
  }
  const visibleGroups = ENV_GROUPS.filter(g => (grouped.get(g.key)?.length ?? 0) > 0);

  // ── Tabs layout ──────────────────────────────────────────────────────────

  const TABS = [
    { label: 'Overview',     icon: <AssessmentIcon  style={{ fontSize: 16 }} />, needsAccount: false },
    { label: 'Errors',       icon: <BugReportIcon   style={{ fontSize: 16 }} />, needsAccount: true  },
    { label: 'Logs',         icon: <ListAltIcon     style={{ fontSize: 16 }} />, needsAccount: true  },
    { label: 'Transactions', icon: <SwapHorizIcon   style={{ fontSize: 16 }} />, needsAccount: true  },
    { label: 'Traces',       icon: <TimelineIcon       style={{ fontSize: 16 }} />, needsAccount: true  },
    { label: 'Deployments',  icon: <CloudUploadIcon   style={{ fontSize: 16 }} />, needsAccount: false },
    { label: 'External',     icon: <HttpIcon          style={{ fontSize: 16 }} />, needsAccount: true  },
    { label: 'Databases',    icon: <StorageIcon       style={{ fontSize: 16 }} />, needsAccount: true  },
    { label: 'Alerts',       icon: <NotificationsIcon style={{ fontSize: 16 }} />, needsAccount: true  },
    { label: 'Service Map',  icon: <AccountTreeIcon   style={{ fontSize: 16 }} />, needsAccount: false },
  ];

  const hasDetailData = rows.length > 0 && rows.some(r => r.accountId !== null);
  const hasRows = rows.length > 0;

  return (
    <Box>
      {/* Top bar: tabs + refresh */}
      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          {TABS.map((tab, idx) => (
            <Tab
              key={tab.label}
              label={
                <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                  {tab.icon}
                  {tab.label}
                </Box>
              }
              disabled={idx > 0 && (tab.needsAccount ? !hasDetailData : !hasRows)}
            />
          ))}
        </Tabs>
        <Box pr={1}>
          <Tooltip title="Refresh APM data">
            <IconButton size="small" onClick={fetchData} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Tab panels */}
      <Box className={classes.tabPanel}>
        {/* Tab 0: Overview */}
        {activeTab === 0 && (
          <>
            <TabInfo
              title="APM Overview — All Environments"
              description="A real-time health summary for every environment of this service, grouped by Production → QA/Staging → Dev. Data is pulled directly from New Relic's APM summary via NerdGraph and refreshes on demand."
              tips={[
                'Apdex: score from 0–1. ≥ 0.9 = Satisfied (green), 0.7–0.9 = Tolerating (orange), < 0.7 = Frustrated (red). A drop here usually means a latency spike.',
                'Error % is the percentage of web transactions that resulted in an error. > 1% warrants investigation; > 5% is severe.',
                'RPM = Requests Per Minute — throughput. A sudden drop alongside normal error rate can indicate traffic loss (failed load balancer or upstream outage).',
                'Alert chip: CRITICAL = alert is firing right now, WARNING = alert threshold was breached but not critical level.',
              ]}
            />
            {visibleGroups.map((group, idx) => {
              const groupRows = grouped.get(group.key) ?? [];
              return (
                <Box key={group.key} className={classes.tableSection}>
                  {idx > 0 && <Divider className={classes.sectionDivider} />}
                  <Table<AppRow>
                    title={
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <span
                          style={{
                            display: 'inline-block',
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: group.color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="h6" style={{ fontWeight: 700 }}>
                          {group.label}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          ({groupRows.length} apps)
                        </Typography>
                      </Box>
                    }
                    columns={overviewColumns}
                    data={groupRows}
                    options={{ paging: false, search: false, padding: 'dense', toolbar: true }}
                  />
                </Box>
              );
            })}
            {rows.length === 0 && (
              <Box p={4} textAlign="center">
                <Typography color="textSecondary">
                  No APM data found for prefix "{prefix}"
                </Typography>
              </Box>
            )}
            {rows.length > 0 && !hasDetailData && (
              <Box mt={2} p={2} style={{ background: 'rgba(255,152,0,0.08)', borderRadius: 4 }}>
                <Typography variant="body2" color="textSecondary">
                  Note: Account ID could not be determined from entity GUIDs — Errors, Logs, and Transaction tabs require account access via NerdGraph.
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Tab 1: Errors */}
        {activeTab === 1 && hasDetailData && (
          <ErrorsPanel rows={rows} proxyUrl={proxyUrl} fetchApi={fetchApi} />
        )}

        {/* Tab 2: Logs */}
        {activeTab === 2 && hasDetailData && (
          <LogsPanel rows={rows} proxyUrl={proxyUrl} fetchApi={fetchApi} />
        )}

        {/* Tab 3: Transactions */}
        {activeTab === 3 && hasDetailData && (
          <TransactionsPanel rows={rows} proxyUrl={proxyUrl} fetchApi={fetchApi} />
        )}

        {/* Tab 4: Distributed Traces */}
        {activeTab === 4 && hasDetailData && (
          <TracesPanel rows={rows} proxyUrl={proxyUrl} fetchApi={fetchApi} />
        )}

        {/* Tab 5: Deployments */}
        {activeTab === 5 && hasRows && (
          <DeployPanel rows={rows} proxyUrl={proxyUrl} fetchApi={fetchApi} />
        )}

        {/* Tab 6: External Services */}
        {activeTab === 6 && hasDetailData && (
          <ExternalServicesPanel rows={rows} proxyUrl={proxyUrl} fetchApi={fetchApi} />
        )}

        {/* Tab 7: Database Queries */}
        {activeTab === 7 && hasDetailData && (
          <DbQueriesPanel rows={rows} proxyUrl={proxyUrl} fetchApi={fetchApi} />
        )}

        {/* Tab 8: Alert History */}
        {activeTab === 8 && hasDetailData && (
          <AlertHistoryPanel rows={rows} proxyUrl={proxyUrl} fetchApi={fetchApi} />
        )}

        {/* Tab 9: Service Map */}
        {activeTab === 9 && hasRows && (
          <ServiceMapPanel rows={rows} proxyUrl={proxyUrl} fetchApi={fetchApi} />
        )}
      </Box>
    </Box>
  );
};
